import { useState } from 'react';
import { Trans, t } from '@lingui/macro';
import isNil from 'lodash/isNil';
import useQueryParams from 'hooks/useQueryParams';

import Flex from '@decisiv/ui-components/lib/components/Flex';
import { H3 } from '@decisiv/ui-components/lib/components/Typography';
import Grid from '@decisiv/ui-components/lib/components/Grid';

import StyledDivider from 'components/Divider';

import { useCurrentUserContext } from 'utils/User';

import { TabButton } from './components/TabButton';

import OemSegments from './OemSegments';
import Teams from './Teams';
import Fleets from './Fleets';
import Depots from './Depots';

type TabKey = 'oem-segments' | 'teams' | 'accounts' | 'depots';
type TabObject = Record<TabKey, { label: string; value: React.ComponentType }>;

function getTabObject(tabKey: TabKey) {
  const TabObjects: TabObject = {
    'oem-segments': { label: t`OEM Segments`, value: OemSegments },
    teams: { label: t`Teams`, value: Teams },
    accounts: { label: t`Accounts`, value: Fleets },
    depots: { label: t`Depots`, value: Depots },
  };

  return TabObjects[tabKey];
}

const ManagementPage = () => {
  const [pageParams, setPageParams] = useQueryParams('management-page', {
    tab: undefined,
  });

  const { isSuperUser, isOemManager, oemSegments, managedSentinelTeams } =
    useCurrentUserContext();

  const oemSegmentsLength = oemSegments?.length ?? 0;
  const hasMultipleOemSegments = isOemManager && oemSegmentsLength > 1;
  const showOemSegmentsTab = (isSuperUser || hasMultipleOemSegments) ?? false;

  const amountOfManagedTeams = managedSentinelTeams?.length ?? 0;
  const showTeamsTab = isSuperUser || amountOfManagedTeams > 1;

  const [selectedTab, setSelectedTab] = useState<TabKey>(() => {
    if (!isNil(pageParams.tab)) {
      const { tab } = pageParams;
      if (tab === 'oem-segments' && showOemSegmentsTab) {
        return 'oem-segments';
      }
      if (tab === 'teams' && showTeamsTab) {
        return 'teams';
      }
      return tab;
    }

    if (showOemSegmentsTab) {
      return 'oem-segments';
    }
    if (showTeamsTab) {
      return 'teams';
    }
    return 'accounts';
  });

  const handleTabChange = (tab: TabKey) => () => {
    setPageParams({ tab });
    setSelectedTab(tab);
  };

  const { label, value: SelectedTabComponent } = getTabObject(selectedTab);

  return (
    <Flex flex={1} flexDirection="column">
      <H3 marginX={2} marginTop={1.5} marginBottom={0.5}>
        <Trans>Management</Trans> / {label}
      </H3>
      <StyledDivider direction="horizontal" />
      <Flex marginX={2} marginY={1} style={{ columnGap: 5 }}>
        {showOemSegmentsTab && (
          <TabButton
            text={t`OEM Segments`}
            onClick={handleTabChange('oem-segments')}
            active={selectedTab === 'oem-segments'}
          />
        )}
        {showTeamsTab && (
          <TabButton
            text={t`Teams`}
            onClick={handleTabChange('teams')}
            active={selectedTab === 'teams'}
          />
        )}
        <TabButton
          text={t`Accounts`}
          onClick={handleTabChange('accounts')}
          active={selectedTab === 'accounts'}
        />
        <TabButton
          text={t`Depots`}
          onClick={handleTabChange('depots')}
          active={selectedTab === 'depots'}
        />
      </Flex>
      <StyledDivider direction="horizontal" />
      <Grid.Row padding={0}>
        <Grid.Column>
          <SelectedTabComponent />
        </Grid.Column>
      </Grid.Row>
    </Flex>
  );
};

export default ManagementPage;
